import React, { useEffect, useRef, useState } from 'react';
import { ArrowUpRight, Github, LayoutDashboard, LogIn, Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import LoretoGlyph from './LoretoGlyph';
import { LINKS } from '../data/content';
import { useAuth } from '../context/AuthContext';
import CurrencySelector from './CurrencySelector';

const NAV = [
  { label: 'Marketplace', to: '/marketplace' },
  { label: 'Creators', to: '/creators' },
  { label: 'Leaderboard', to: '/leaderboard' },
  { label: 'Loreto API', to: '/api' },
  { label: 'Git Timeline', to: '/git-timeline' },
  { label: 'Field Papers', to: '/papers' },
];

export default function Navbar({ solid = false }) {
  const [scrolled, setScrolled] = useState(false);
  // Below md the link row is hidden, and for a long time nothing replaced it:
  // every nav destination on the site was unreachable from a phone.
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { isAuthed, user } = useAuth();
  const panel = useRef(null);
  const toggle = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Navigating is the commonest way to finish with the menu, and leaving it
  // open over the page you just asked for is the commonest way to get that
  // wrong. Close on every route change, whatever caused it.
  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      toggle.current?.focus();          // don't strand the focus ring in a closed panel
    };
    const onPointer = (e) => {
      if (!panel.current?.contains(e.target) && !toggle.current?.contains(e.target)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [open]);

  // An open panel on a transparent header is unreadable over a photograph.
  const showSolid = solid || scrolled || open;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        showSolid
          ? 'md:backdrop-blur-md bg-[var(--nav-bg)] md:bg-[var(--nav-bg-blur)] border-b border-[var(--border)]'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-6 lg:gap-10">
        <Link to="/" className="flex items-center gap-2.5 group shrink-0">
          <LoretoGlyph size={26} className="shrink-0" />
          <span className="serif text-[1.5rem] leading-none tracking-tight">Loreto</span>
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          {NAV.map((l) => {
            const active = location.pathname === l.to;
            return (
              <Link
                key={l.to}
                to={l.to}
                className={`text-[0.86rem] whitespace-nowrap transition-colors duration-200 ${
                  active ? 'text-[var(--text)]' : 'text-[var(--text-2)] hover:text-[var(--text)]'
                }`}
              >
                {l.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <button
            ref={toggle}
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="btn-ghost !py-2 !px-3 md:hidden"
            aria-label={open ? 'Close the menu' : 'Open the menu'}
            aria-expanded={open}
            aria-controls="nav-menu"
          >
            {open ? <X size={16} /> : <Menu size={16} />}
          </button>
          <CurrencySelector className="hidden sm:inline-flex" />
          <a
            href={LINKS.github}
            target="_blank"
            rel="noreferrer"
            className="btn-ghost !py-2 !px-3.5 !text-[0.82rem] hidden sm:inline-flex"
            aria-label="GitHub"
          >
            <Github size={14} />
            <span className="hidden md:inline">GitHub</span>
          </a>
          {isAuthed ? (
            <Link to="/dashboard" className="btn-primary !py-2 !px-4 !text-[0.82rem] whitespace-nowrap shrink-0">
              <LayoutDashboard size={14} />
              <span className="max-w-[110px] truncate">{user?.name || user?.email?.split('@')[0] || 'Dashboard'}</span>
              <ArrowUpRight size={14} />
            </Link>
          ) : (
            <Link to="/dashboard" className="btn-primary !py-2 !px-4 !text-[0.82rem] whitespace-nowrap shrink-0">
              <LogIn size={14} />
              Sign in
              <ArrowUpRight size={14} />
            </Link>
          )}
        </div>
      </div>

      {/* The phone menu. Carries the same destinations as the desktop row plus
          the two controls that are hidden at this width, so nothing on the
          site is reachable on a laptop and unreachable on a phone. */}
      <div
        id="nav-menu"
        ref={panel}
        hidden={!open}
        className="md:hidden border-t border-[var(--border)] bg-[var(--nav-bg)] backdrop-blur-md"
      >
        <nav className="max-w-[1400px] mx-auto px-6 py-3 flex flex-col">
          {NAV.map((l) => {
            const active = location.pathname === l.to;
            return (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                aria-current={active ? 'page' : undefined}
                className={`py-3 text-[0.95rem] border-b border-[var(--border)] last:border-b-0 transition-colors ${
                  active ? 'text-[var(--accent)]' : 'text-[var(--text-2)] hover:text-[var(--text)]'
                }`}
              >
                {l.label}
              </Link>
            );
          })}
          <div className="flex items-center gap-3 pt-4 pb-1 sm:hidden">
            <CurrencySelector />
            <a
              href={LINKS.github}
              target="_blank"
              rel="noreferrer"
              className="btn-ghost !py-2 !px-3.5 !text-[0.82rem]"
            >
              <Github size={14} /> GitHub
            </a>
          </div>
        </nav>
      </div>
    </header>
  );
}
